/**
 * ImportFieldsDialog Component
 *
 * Modal for importing fields from various sources into a schema.
 * Features:
 * - Extensible source tabs (JSON, Existing Schema, future: databases, webservices)
 * - Field selection with checkboxes
 * - Conflict detection for existing fields
 * - Merge with overwrite support
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, FileJson, Database } from 'lucide-react';
import { SchemaField } from '@/services/api/schemaApi';
import { FieldSelectionList } from './FieldSelectionList';
import { JsonImportSource, ParsedFieldGroups } from './import-sources/JsonImportSource';
import { SchemaImportSource } from './import-sources/SchemaImportSource';

type ImportSource = 'json' | 'schema';

interface ImportFieldsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingFields: SchemaField[];
  onImport: (fields: SchemaField[]) => void;
  excludeSchemaId?: string; // Exclude current schema when importing from existing
}

export const ImportFieldsDialog: React.FC<ImportFieldsDialogProps> = ({
  open,
  onOpenChange,
  existingFields,
  onImport,
  excludeSchemaId,
}) => {
  // State
  const [activeSource, setActiveSource] = useState<ImportSource>('json');
  const [parsedFields, setParsedFields] = useState<SchemaField[]>([]);
  const [parsedGroups, setParsedGroups] = useState<ParsedFieldGroups | undefined>(undefined);
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());

  // Get existing field names for conflict detection
  const existingFieldNames = existingFields.map((f) => f.name);

  // Reset state when dialog opens/closes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        // Reset state on close
        setParsedFields([]);
        setParsedGroups(undefined);
        setSelectedFieldIds(new Set());
        setActiveSource('json');
      }
      onOpenChange(isOpen);
    },
    [onOpenChange]
  );

  // Handle fields parsed from any source
  const handleFieldsParsed = useCallback((fields: SchemaField[], groups?: ParsedFieldGroups) => {
    setParsedFields(fields);
    setParsedGroups(groups);
    // Auto-select all fields by default
    setSelectedFieldIds(new Set(fields.map((f) => f.id)));
  }, []);

  // Handle import button click
  const handleImport = useCallback(() => {
    const fieldsToImport = parsedFields.filter((f) =>
      selectedFieldIds.has(f.id)
    );

    if (fieldsToImport.length === 0) return;

    onImport(fieldsToImport);
    handleOpenChange(false);
  }, [parsedFields, selectedFieldIds, onImport, handleOpenChange]);

  // Get count of selected fields
  const selectedCount = selectedFieldIds.size;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Import Fields
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Source tabs */}
          <Tabs
            value={activeSource}
            onValueChange={(value) => {
              setActiveSource(value as ImportSource);
              // Clear parsed fields when switching sources
              setParsedFields([]);
              setSelectedFieldIds(new Set());
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="json" className="flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                JSON
              </TabsTrigger>
              <TabsTrigger value="schema" className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                Existing Schema
              </TabsTrigger>
            </TabsList>

            <TabsContent value="json" className="mt-4">
              <JsonImportSource onFieldsParsed={handleFieldsParsed} />
            </TabsContent>

            <TabsContent value="schema" className="mt-4">
              <SchemaImportSource
                onFieldsLoaded={(fields) => handleFieldsParsed(fields)}
                excludeSchemaId={excludeSchemaId}
              />
            </TabsContent>
          </Tabs>

          {/* Field selection list (shown after parsing) */}
          {parsedFields.length > 0 && (
            <div className="border-t pt-4">
              <FieldSelectionList
                fields={parsedFields}
                selectedIds={selectedFieldIds}
                existingFieldNames={existingFieldNames}
                onSelectionChange={setSelectedFieldIds}
                groups={parsedGroups}
              />
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={selectedCount === 0}
            className="bg-primary hover:bg-primary/90"
          >
            Import {selectedCount} Field{selectedCount !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFieldsDialog;
